import * as z from 'zod';

import {
  type AnyFeedstock,
  ZodAnyFeedstock,
} from './feedstock.js';
import { feedstockDatabase } from './feedstock-db.js';
import type { ConversionProcess } from './shared.js';
import {
  type Country,
  ZodConversionProcess,
  ZodCountry,
} from './shared.js';

export enum NeatFuelVolumeUnit {
  Liters = 'liters',
  Gallons = 'gallons',
}

export const ZodNeatFuelUnit = z.enum(NeatFuelVolumeUnit);

export const ZodProductNeatFuel = z.union([
  z.object({ volumeOfNeatFuel: z.literal(undefined).optional() }),
  z.object({ volumeOfNeatFuel: z.number().min(0.0001), volumeOfNeatFuelUnit: ZodNeatFuelUnit }),
]);

export enum BaselineCarbonIntensity {
  GCORSIAJetFuel = '89g',
  GEURED = '94g',
  GRSBGlobal = '90g',
  GCORSIAAviationGasoline = '95g',
}

export const baselineCarbonIntensityToNumber = (v: BaselineCarbonIntensity) => Number(v.slice(0, -1));

export const ZodBaselineCarbonIntensity = z.enum(BaselineCarbonIntensity);

export type VolumeOfNeatFuel
  = | { volumeOfNeatFuel: undefined }
    | { volumeOfNeatFuel: number; volumeOfNeatFuelUnit: NeatFuelVolumeUnit };

export type Product = {
  feedStock: AnyFeedstock;
  quantityMT: number;
  countries: Country[];
  conversionProcess: ConversionProcess;
  lowerHeatingValue: number;
  jetFuelBaselineCarbonIntensity: BaselineCarbonIntensity;
} & VolumeOfNeatFuel;

export const ZodProduct = z.object({
  feedStock: ZodAnyFeedstock,
  quantityMT: z.number().min(0),
  countries: z.array(ZodCountry),
  conversionProcess: ZodConversionProcess,
  lowerHeatingValue: z.number().min(0),
  jetFuelBaselineCarbonIntensity: ZodBaselineCarbonIntensity,
}).and(ZodProductNeatFuel)
  .superRefine((data, ctx) => {
    const feedstock = feedstockDatabase.find(f => f.name === data.feedStock.name);
    if (feedstock === undefined) {
      throw new Error(`Feedstock of name ${data.feedStock.name} not found`);
    }

    const expectedCorsia = feedstock.corsiaDefaultIlucLcaLteZeroCb({
      conversionProcess: data.conversionProcess,
      feedstockCountries: data.countries,
    });

    if (data.feedStock.isCorsiaDefaultIlucLcaLteZero !== expectedCorsia) {
      ctx.addIssue({
        code: 'custom',
        message: `isCorsiaDefaultIlucLcaLteZero should be ${expectedCorsia}`,
        input: data.feedStock.isCorsiaDefaultIlucLcaLteZero,
        path: ['feedStock', 'isCorsiaDefaultIlucLcaLteZero'],
      });
    }
  });
